import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import { AddButton } from "../../../../utils/Custom/Button";
import { submitSiteDelete } from "../../../../utils/commonComponent/delete";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import ListSearchSection from "../../../../utils/Custom/ListSearchSection";
import ListView from "../../../../utils/Custom/listView";
import { getPermissions } from "../../../Pages/rbac/hasAccess";
import { stageStatuses } from "./editInterviewSchedule";

const headers = [
  "id",
  "SchedId",
  "candidateName",
  "position",
  "client",
  "round",
  "interviewDate",
  "interviewTime",
  "Interviewer",
  "interviewStatus",
];

const fields = [
  { value: "candidateName", label: "Candidate Name" },
  { value: "position", label: "Position" },
  { value: "client", label: "Client" },
  { value: "Interviewer", label: "Interviewer" },
  { value: "interviewStatus", label: "Interview Status" },
  { value: "mode", label: "Interview Mode" },
];

const InterviewScheduleList = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);

  const handleEdit = (row) => navigate(`/talent-acquisition/editInterviewSchedule/${row.id}`);
  const handleDelete = (row) => submitSiteDelete("talentAcqInterview", row.id);

  const [scheduleList, setScheduleList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [searchBy, setSearchBy] = React.useState(sessionStorage.getItem("searchBy") || "candidateName");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const [searched, setSearched] = React.useState(sessionStorage.getItem("searched") === "true" || false);
  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const [modeMaster, setModeMaster] = useState([]);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const records = await fetchQuery(
          "SELECT id, type, Value FROM talentAcquisitionMaster WHERE type = 7 AND status = 1"
        );
        if (records && records.length > 0) {
          setModeMaster(records.map((m) => ({ id: m.id, name: m.Value })));
        }
      } catch (e) {
        console.error("Error loading interview schedule masters:", e);
      }
    };
    loadMasters();
  }, []);

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getWhereClause = () => {
    const key = searchKey.trim();
    let whereConditions = ["status = 1"];

    if (searchBy && key) {
      if (searchBy === "interviewStatus") {
        const found = stageStatuses.filter(m => m.name.toLowerCase().includes(key.toLowerCase())).map(m => m.id);
        if (found.length > 0) {
          whereConditions.push(`interviewStatus IN (${found.join(",")})`);
        } else {
          whereConditions.push("1 = 0");
        }
      } else if (searchBy === "mode") {
        const found = modeMaster.filter(m => m.name.toLowerCase().includes(key.toLowerCase())).map(m => m.id);
        if (found.length > 0) {
          whereConditions.push(`mode IN (${found.join(",")})`);
        } else {
          whereConditions.push("1 = 0");
        }
      } else {
        whereConditions.push(`${searchBy} LIKE '%${key}%'`);
      }
    }

    return `WHERE ${whereConditions.join(" AND ")}`;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    const pagination =
      rowsPerPage !== -1
        ? `LIMIT ${rowsPerPage} OFFSET ${rowsPerPage * currentPage}`
        : "";
    return `SELECT * FROM talentAcqInterview ${whereClause} ORDER BY id DESC ${pagination};`;
  };

  const getCountQuery = () => {
    return `SELECT COUNT(*) AS totalRows FROM talentAcqInterview ${getWhereClause()};`;
  };

  const fetchData = async () => {
    try {
      const [data, countResult] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
      ]);

      const mapped = (data || []).map((row) => ({
        ...row,
        mode: modeMaster.find((m) => m.id == row.mode)?.name || row.mode || "",
        interviewStatus: stageStatuses.find((m) => m.id == row.interviewStatus)?.name || row.interviewStatus || "",
      }));

      setScheduleList(mapped);
      setTotalRows(countResult?.[0]?.totalRows || 0);
    } catch (error) {
      console.error("Error fetching interview schedule data:", error);
    }
  };

  const handleSearch = () => {
    if (!searchBy || !searchKey.trim()) {
      alert("Please select a search field and enter a search value.");
      return;
    }
    fetchData();
    setSearched(true);
    sessionStorage.setItem("searchBy", searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearched(false);
    setSearchBy("candidateName");
    setSearchKey("");
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    setCurrentPage(0);
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    if (searched === false) {
      fetchData();
    }
  }, [searched]);

  return (
    <>
      {(permissions.create || permissions.allAccess) && (
        <AddButton
          id="btn-add-schedule"
          to="/talent-acquisition/editInterviewSchedule/0"
          label="Add Interview Schedule"
          icon={<AddIcon fontSize="small" />}
        />
      )}
      <ListSearchSection
        title="Search Interview Schedules"
        fields={fields}
        onSearch={handleSearch}
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        clearSearch={clearSearch}
        searched={searched}
        setSearched={setSearched}
      />
      <ListView
        headers={headers}
        data={scheduleList}
        actions={{
          edit: handleEdit,
          delete: handleDelete,
        }}
        showCheckbox={true}
        deleteTableName="talentAcqInterview"
        moduleId={props.moduleId}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPaginationChange={handlePaginationChange}
      />
    </>
  );
};

export default InterviewScheduleList;
